<template>
    <div height="medium" width="large">
        <div>
            <h2 class="heading">{{ $t('modals.patient_profile.profile.detail_form.tabs.hipaa') }}</h2>
        </div>

        <div class="upload-container">
            <div class="hipaa-title">
                <h3>{{ $t('modals.patient_profile.profile.hippa_detail.upload.head') }}</h3>
            </div>
            <form-wrapper class="practice-information-form">
                <form-row>
                    <form-cell width="half">
                        <label class="form-field__label">
                            {{ $t('forms.patient_profile.fields.hipaa.label') }}
                        </label>
                        <text-field
                            :placeholder="$t('forms.patient_profile.fields.hipaa.insurance_plans.placeholder')"
                            :isRequired="false"
                            :formData="data"
                            valuePath="authorizationName"
                            @change="change($event)"
                        ></text-field>
                    </form-cell>
                    <form-cell width="half">
                        <label class="form-field__label">
                            {{ $t('forms.patient_profile.fields.hipaa.document.label') }}
                        </label>
                        <text-field
                            :placeholder="$t('forms.patient_profile.fields.hipaa.document.placeholder')"
                            :isRequired="false"
                            :formData="data"
                            :minDate="minSelectableDate"
                            valuePath="expiryDate"
                            type="date"
                            @change="change($event)"
                        ></text-field>
                    </form-cell>
                </form-row>
                <form-row>
                    <form-cell width="half">
                        <label class="form-field__label">
                            {{ $t('forms.patient_profile.fields.hipaa.provider.label') }}
                        </label>
                        <div v-clickaway="closeDropDown" class="auth-prov-wrapper" :class="{ focused: isAuthProvFocused }">
                            <div class="chips">
                                <span
                                    v-for="user in selectedAuthProviders"
                                    :key="user.id"
                                    class="chip"
                                    @click.stop="unselectUser(user)"
                                >
                                    {{ `${user.firstName} ${user.lastName}` }}
                                    <common-icon name="close" size="tiny" class="remove"></common-icon>
                                </span>
                            </div>
                            <input
                                v-model="searchTerm"
                                type="text"
                                class="auth-prov"
                                :placeholder="searchProviderPlaceholder"
                                @keyup="inputChange"
                            />
                        </div>

                        <common-dropdown
                            :recalculatePosition="true"
                            :isOpen="showAuthProv"
                            :hasArrow="false"
                            arrowPositionY="top"
                            arrowPositionX="right"
                        >
                            <div class="network-users">
                                <div
                                    v-for="(user, index) in items"
                                    :key="index"
                                    :class="getNetworkUsersClass(user)"
                                    @click="selectUser(user)"
                                >
                                    <div>
                                        <common-user-avatar
                                            size="user-menu"
                                            :user="user.user"
                                            :isUserProfileLinkEnabled="false"
                                        ></common-user-avatar>
                                    </div>
                                    <div>
                                        <p>
                                            {{ user.user ? user.user.firstName + ' ' + user.user.lastName : user.practice.name }}
                                        </p>
                                        <p v-if="user.user">{{ providerSpecialization(user?.specialization) }}</p>
                                    </div>
                                </div>

                                <div v-if="isLoading" class="loading-spinner">
                                    <span>{{ $t('modals.import_contacts_modal.loading') }}</span>
                                    <common-logo-spinner :width="56"></common-logo-spinner>
                                </div>

                                <div v-else-if="hasSearched && items.length === 0">
                                    <p class="search-network-members__message">
                                        {{ $t('modals.search_network_members.no_member_found') }}
                                    </p>
                                </div>
                            </div>
                        </common-dropdown>
                    </form-cell>
                    <form-cell width="half">
                        <label class="form-field__label">
                            {{ $t('forms.patient_profile.fields.hipaa.comment.label') }}
                        </label>
                        <select-input
                            class="schedule-apply__input"
                            :placeholder="$t('forms.patient_profile.fields.hipaa.comment.placeholder')"
                            :hasAutofocus="true"
                            :options="scopeOfAuthorizationOptions"
                            :value="scopeId"
                            @change="changeScope($event)"
                        ></select-input>
                    </form-cell>
                </form-row>
            </form-wrapper>

            <div class="upload-box">
                <div class="upload-icon">
                    <common-icon name="chose" size="ultra-large" @click="openFilePicker"></common-icon>
                </div>
                <input ref="fileInput" type="file" class="upload-input" @change="handleFileChange" />
                <p class="upload-text">{{ $t('modals.patient_profile.profile.hippa_detail.upload.drop_text') }}</p>
                <p class="upload-subtext">{{ $t('modals.patient_profile.profile.hippa_detail.upload.subtext') }}</p>
                <button class="upload-subtitle" @click="openFilePicker">
                    <common-icon name="chose" size="small"></common-icon>
                    <span>{{ $t('modals.patient_profile.profile.request.hipaa.choose_file') }}</span>
                </button>
            </div>

            <div v-if="selectedFile" class="selected-files-wrapper">
                <div class="selected-file">
                    <common-icon name="file" size="medium" class="selected-file__icon"></common-icon>

                    <div class="selected-file__info">
                        <p class="selected-file__name">
                            {{ selectedFile.name }}
                        </p>

                        <p class="selected-file__size">
                            {{ formatFileSize(selectedFile.size / 1024) }}
                        </p>
                    </div>

                    <button type="button" class="selected-file__remove" @click="removeFile">
                        {{ $t('common.microcopy.remove') }}
                    </button>
                </div>
            </div>

            <p v-if="fileErrorMessage" class="upload-subtext_error">
                {{ fileErrorMessage }}
            </p>

            <div class="button-row">
                <button class="upload-btn" :disabled="!selectedFile" @click="saveHipaaForm">
                    <common-icon name="insure" size="medium"></common-icon>
                    {{ $t('modals.patient_profile.profile.hippa_detail.upload.button') }}
                </button>
                <a href="/files/HIPAA-Journal-sample-HIPAA-release-form.pdf" download>
                    <button class="down">
                        <common-icon name="navigation" size="small"></common-icon>
                        {{ $t('modals.patient_profile.profile.hippa_detail.upload.download') }}
                    </button>
                </a>
            </div>
        </div>

        <div class="hipaa-container">
            <div class="hipaa-header">
                <span class="hipaa-title">
                    <common-icon name="insure" size="medium"></common-icon>
                    <h3>{{ $t('modals.patient_profile.profile.hippa_detail.head') }}</h3>
                </span>
                <span class="active-form-badge">
                    {{ $t('modals.patient_profile.profile.hippa_detail.active', { n: activeHipaaFormsCount }) }}
                </span>
            </div>

            <div v-if="!availHipaaForms.length"></div>
            <div v-for="hipaa in availHipaaForms" v-else :key="hipaa.id" class="hipaa-card">
                <div class="main">
                    <div class="hipaa-card-header">
                        <h4>{{ hipaa.authorizationName }}</h4>
                        <span class="status" :class="`status-${getHipaaStatus(hipaa.expiryDate)}`">
                            {{ $t(`modals.patient_profile.profile.hippa_detail.header.${getHipaaStatus(hipaa.expiryDate)}`) }}
                        </span>
                    </div>
                    <div class="hipaa-actions">
                        <button class="btn-view" @click="viewHipaa(hipaa)">
                            <common-icon name="view" size="small"></common-icon>
                            {{ $t('modals.patient_profile.profile.hippa_detail.button.view') }}
                        </button>
                        <button class="btn-download" @click="downloadHipaa(hipaa)">
                            <common-icon name="download" size="small"></common-icon>
                            {{ $t('modals.patient_profile.profile.hippa_detail.button.download') }}
                        </button>
                    </div>
                </div>
                <div class="hipaa-details">
                    <div class="hipaa-left">
                        <div class="main-data">
                            <p class="file">{{ $t('modals.patient_profile.profile.hippa_detail.file.file_title') }}</p>
                            <p class="file-detail">{{ hipaa.fileName }}</p>
                        </div>
                        <div class="main-data">
                            <p class="file">{{ $t('modals.patient_profile.profile.hippa_detail.file.upload_title') }}</p>
                            <p class="file-detail">{{ dateFormate(hipaa.createdDateTime) }}</p>
                        </div>
                        <p class="scope-tags">
                            <strong>{{ $t('modals.patient_profile.profile.request.hipaa.scope') }}</strong>
                            {{ hipaa.scope }}
                        </p>
                        <span v-for="provider in hipaa.authorizedProviders" class="scope-tag">
                            {{ provider.firstName }} {{ provider.lastName }}
                        </span>
                    </div>

                    <div class="hipaa-right">
                        <div class="main-data">
                            <p class="file">{{ $t('modals.patient_profile.profile.hippa_detail.file.size_title') }}</p>
                            <p class="file-detail">{{ formatFileSize(hipaa.size) }}</p>
                        </div>
                        <div class="main-data">
                            <p class="file">{{ $t('modals.patient_profile.profile.hippa_detail.file.expires_title') }}</p>
                            <p class="file-detail">
                                {{ dateFormate(hipaa.expiryDate) }}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <common-notification ref="commonSuccessNotificationChild" :content="notificationMessage"></common-notification>
    </div>
</template>

<script>
    import FormMixin from 'mixins/FormMixin'
    import HandleRequestMixin from 'mixins/HandleRequestMixin'
    import debounce from 'lodash.debounce'
    import config from '../../../config'
    import ProviderDatabaseAPI from 'api/ProviderDatabaseAPI'
    import PatientRecordsAPI from '../../../api/PatientRecordsAPI'
    import dayjs from 'dayjs'
    import { dateFormate } from '~/helpers'
    import FileMixin from 'mixins/FileMixin'

    export default {
        mixins: [FormMixin, HandleRequestMixin, FileMixin],

        props: {
            availHipaaForms: { type: Array, required: true },
            hideHeading: { type: Boolean, default: false },
        },

        data() {
            return {
                scopeOfAuthorizationOptions: [
                    { label: 'Complete Medical Records', value: 1 },
                    { label: 'General Medical (No Mental Health)', value: 2 },
                    { label: 'Mental Health Records Only', value: 3 },
                    { label: 'Lab & Imaging Only', value: 4 },
                    { label: 'Prescription History Only', value: 5 },
                ],
                scopeId: null,
                selectedFile: null,
                fileErrorMessage: null,
                showAuthProv: false,
                isAuthProvFocused: false,
                selectedAuthProviders: [],
                items: [],
                searchTerm: '',
                isLoading: false,
                hasSearched: false,
                notificationMessage: '',
            }
        },

        computed: {
            searchProviderPlaceholder() {
                return this.selectedAuthProviders.length ? '' : this.$t('forms.patient_profile.fields.hipaa.provider.placeholder')
            },
            selectedScope() {
                return this.scopeOfAuthorizationOptions.find((item) => item.value === this.scopeId)
            },
            minSelectableDate() {
                return dayjs().format('YYYY-MM-DD')
            },
            activeHipaaFormsCount() {
                const today = dayjs()

                return this.availHipaaForms.filter((form) => !dayjs(form.expiryDate).isBefore(today, 'day')).length
            },
        },

        methods: {
            dateFormate,
            getNetworkUsersClass(user) {
                return ['network-user', { 'network-user-selected': user ? this.selectedAuthProviders[user.id] : false }]
            },
            changeScope(event) {
                this.scopeId = event
            },
            openFilePicker() {
                this.$refs.fileInput.click()
            },
            handleFileChange(event) {
                const file = event.target.files[0]
                if (!file) return
                this.selectedFile = file
            },
            removeFile() {
                this.selectedFile = null
                this.fileErrorMessage = ''
                this.$refs.fileInput.value = ''
            },
            closeDropDown() {
                this.showAuthProv = false
                this.isAuthProvFocused = false
            },
            inputChange: debounce(function () {
                this.emitEvent({ name: 'input-change' })
                if (this.searchTerm.length < 3) {
                    this.items = []
                    this.hasSearched = false
                    this.isLoading = false
                    this.showAuthProv = false
                    return
                }
                this.showAuthProv = true
                this.isAuthProvFocused = true
                this.hasSearched = true
                this.isLoading = true

                ProviderDatabaseAPI.search({
                    query: this.searchTerm,
                })
                    .then((val) => {
                        this.items = Array.isArray(val) ? val : []
                    })
                    .catch(() => {
                        this.items = []
                    })
                    .finally(() => {
                        this.isLoading = false
                    })
            }, config.DEBOUNCE.DEFAULT),

            selectUser(user) {
                if (!user) return

                const exists = this.selectedAuthProviders.find((item) => item.id === user.id)

                if (exists) return

                this.selectedAuthProviders.push({
                    id: user.id,
                    firstName: user.firstName,
                    lastName: user.lastName,
                    npi: user.npi,
                    businessEmail: user.businessEmail,
                })

                this.searchTerm = ''
                this.items = []
            },

            unselectUser(ev) {
                this.selectedAuthProviders.forEach((user, index) => {
                    if (user.id === ev.id) {
                        this.selectedAuthProviders.splice(index, 1)
                    }
                })
            },

            providerSpecialization(value) {
                if (!value) return ''

                return value
                    .split('-')
                    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                    .join(' ')
            },
            getBase64(file) {
                return new Promise((resolve, reject) => {
                    const reader = new FileReader()
                    reader.readAsDataURL(file)
                    reader.onload = () => resolve(reader.result)
                    reader.onerror = (error) => reject(error)
                })
            },

            async saveHipaaForm() {
                this.notificationMessage = ''

                if (!this.selectedFile) return

                const UploadsAPI = (await import('api/UploadsAPI')).default
                const [uploaded] = await UploadsAPI.uploadFiles([this.selectedFile], 'patient-hipaa')
                const payload = {
                    patientId: Number(this.$route.params.id),
                    authorizationName: this.data.authorizationName,
                    scope: this.selectedScope?.label,
                    fromUserId: Number(this.$auth.getActiveMember()?.id),
                    fromPracticeId: Number(this.$auth.getUser()?.practiceId),
                    expiryDate: this.data.expiryDate,
                    authorizedProviders: this.selectedAuthProviders,
                    file: {
                        uploadKey: uploaded.uploadKey,
                        fileName: uploaded.fileName,
                        size: uploaded.size,
                    },
                }
                PatientRecordsAPI.createHipaaForm(payload).then(() => {
                    this.notificationMessage = this.$t('modals.patient_profile.profile.hippa_detail.notification')
                    this.$refs.commonSuccessNotificationChild.showNotification()
                    this.resetHipaaForm()
                    this.emitEvent({ name: 'refresh-hipaa' })
                })
            },

            resetHipaaForm() {
                this.data.authorizationName = ''
                this.data.expiryDate = null
                this.scopeId = null

                this.selectedAuthProviders = []
                this.items = []
                this.searchTerm = ''

                this.selectedFile = null
                this.fileErrorMessage = null

                if (this.$refs.fileInput) {
                    this.$refs.fileInput.value = ''
                }
            },

            getHipaaStatus(expiryDate) {
                return dayjs(expiryDate).isBefore(dayjs(), 'day') ? 'inactive' : 'active'
            },

            formatFileSize(kb) {
                if (!kb) return '0 KB'
                if (kb < 1024) return `${Math.round(kb)} KB`
                return `${(kb / 1024).toFixed(1)} MB`
            },

            viewHipaa(hipaa) {
                const routeData = this.$router.resolve({
                    name: 'image.preview',
                    query: { url: hipaa.url },
                })
                window.open(routeData.href, '_blank')
            },

            downloadHipaa(hipaa) {
                this.downloadFile(hipaa.url, hipaa.fileName)
            },
        },
    }
</script>

<style scoped lang="scss">
    @import 'styles/setup/_variables.scss';
    @import 'styles/setup/_mixins.scss';
    .heading {
        font-size: 16px;
        color: oklch(0.21 0.034 264.665);
        font-weight: 500;
        margin-bottom: 14px;
    }
    .hipaa-container {
        display: flex;
        flex-direction: column;
        margin-bottom: 20px;
        padding: 20px 28px;
        border-radius: 12px;
        border: 1px solid #ddd;
        background-color: #fff;
    }
    .hipaa-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        width: 100%;
        margin-bottom: 16px;
    }
    .hipaa-title {
        display: flex;
        flex-direction: row;
        align-items: center;
        margin: 10px 0 20px;
        gap: 10px;
        h3 {
            margin: 0;
            font-size: 16px;
            font-weight: 400;
            color: $color-dark-2;
        }
        i {
            color: #155dfc;
        }
    }
    .active-form-badge {
        font-size: 10.5px;
        background: #dbeafe;
        color: #193cb8;
        padding: 1.75px 7px;
        border-radius: 6.75px;
        font-weight: 500;
    }
    .hipaa-card {
        border: 1px solid #0000001a;
        border-radius: 8.75px;
        padding: 14px;
        background: #fff;
        margin-bottom: 12px;
    }
    .hipaa-card-header {
        display: flex;
        align-items: center;
        gap: 8px;
        h4 {
            font-size: 16px;
            color: oklch(0.21 0.034 264.665);
            font-weight: 500;
        }
    }
    .status {
        font-size: 11px;
        font-weight: 500;
        padding: 1px 7px;
        border-radius: 6px;
    }

    .status-active {
        background: #d1fae5;
        color: #065f46;
    }

    .status-inactive {
        background: #fee2e2;
        color: #991b1b;
    }
    .main {
        display: flex;
        align-items: center;
        justify-content: space-between;
        width: 100%;
    }
    .hipaa-actions {
        display: flex;
        gap: 8px;
    }
    .btn-view,
    .btn-download {
        font-size: 13px;
        font-weight: 500;
        color: oklch(0.21 0.034 264.665);
        border: 1px solid #ddd;
        background: #fff;
        padding: 6px 10px;
        border-radius: 7px;
        cursor: pointer;
        gap: 10px;
        display: flex;
        align-items: center;
        justify-content: center;
    }
    .hipaa-details {
        display: flex;
        justify-content: space-between;
        flex-wrap: wrap;
        width: 58%;
    }
    .main-data {
        display: flex;
        align-items: center;
        margin-bottom: 4px;
    }
    .file {
        color: $color--txt-header-3;
        font-weight: 500;
        font-size: 14px;
        margin-right: 8px;
    }
    .file-detail {
        font-size: 12px;
        margin: 4px 0;
        color: #333;
    }
    .scope-tags {
        font-size: 14px;
        color: $color-dark-2;
        font-weight: 500;
        display: flex;
        align-items: center;
        gap: 6px;
    }
    .scope-tag {
        display: inline-block;
        margin-top: 6px;
        border: 1px solid #e9ebef;
        color: oklch(0.21 0.034 264.665);
        font-weight: 500;
        font-size: 11px;
        padding: 1px 7px;
        border-radius: 6px;
    }
    .upload-container {
        display: flex;
        flex-direction: column;
        margin-bottom: 20px;
        padding: 20px 28px;
        border-radius: 12px;
        border: 1px solid #ddd;
        background-color: #fff;
    }
    .upload-box {
        margin-top: 20px;
        border: 2px dashed #ccc;
        border-radius: 8px;
        padding: 30px 0px 20px;
        text-align: center;
        margin-bottom: 20px;
        background: #fff;
        cursor: pointer;
        position: relative;
    }
    .upload-icon {
        i {
            color: #99a1af;
        }
    }
    .upload-input {
        width: 100%;
        opacity: 0;
        position: absolute;
        left: 0;
        top: 0;
        height: 100%;
    }
    .upload-text {
        font-size: 16px;
        color: oklch(0.21 0.034 264.665);
        font-weight: 500;
    }
    .upload-subtext {
        font-size: 12px;
        color: #4a5565;
        margin-bottom: 20px;
    }
    .upload-subtitle {
        background: #fff;
        border: 1px solid #d1d5dc;
        padding: 7px 14px;
        border-radius: 8px;
        gap: 7px;
        color: #364153;
        font-size: 14px;
        font-weight: 500;
        display: inline-flex;
        align-items: center;
        cursor: pointer;
        &:hover {
            background: #f9fafb;
        }
        box-shadow: 0 1px 3px 0 #0000001a, 0 1px 2px -1px #0000001a;

        i {
            color: #364153;
        }
    }
    .selected-files-wrapper {
        width: 100%;
        margin-top: 12px;
    }

    .selected-file {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 12px;
        background: #f0fdf4;
        border: 1px solid #bbf7d0;
        border-radius: 8px;

        &__icon {
            flex-shrink: 0;
            color: #16a34a;
        }

        &__info {
            flex: 1;
            min-width: 0;
        }

        &__name {
            margin: 0;
            color: #166534;
            font-size: 14px;
            font-weight: 500;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }

        &__size {
            margin: 2px 0 0;
            color: #16a34a;
            font-size: 12px;
        }

        &__remove {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            height: 32px;
            padding: 0 12px;
            border: 0;
            border-radius: 6px;
            gap: 6px;
            background: transparent;
            color: #dc2626;
            font-size: 14px;
            font-weight: 500;
            line-height: 1;
            white-space: nowrap;
            cursor: pointer;
            transition: all 0.2s ease;

            &:hover {
                color: #b91c1c;
                background: #f3f4f6;
            }

            &:focus-visible {
                outline: none;
                box-shadow: 0 0 0 3px #3b82f633;
            }

            &:disabled {
                pointer-events: none;
                opacity: 0.5;
            }
        }
    }
    .button-row {
        text-align: start;
        margin: 15px 0;
    }

    .upload-btn {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        height: 36px;
        padding: 8px 16px;
        border: none;
        border-radius: 6px;
        background: #16a34a;
        color: #fff;
        font-size: 14px;
        font-weight: 500;
        line-height: 1;
        white-space: nowrap;
        cursor: pointer;
        transition: all 0.2s ease;

        &:hover {
            background: #15803d;
        }

        &:focus-visible {
            outline: none;
            box-shadow: 0 0 0 3px #3b82f633;
        }

        &:disabled {
            pointer-events: none;
            opacity: 0.5;
        }

        common-icon {
            flex-shrink: 0;
        }
    }
    .down {
        color: $color--accent-19;
        margin-left: 14px;
        font-size: 12px;
        font-weight: 500;
        display: inline-flex;
        align-items: baseline;
        gap: 6px;
    }

    .down:hover {
        text-decoration: underline;
        text-decoration-color: $color--accent-19;
        text-underline-offset: 3px;
    }
    .btn-view:hover,
    .btn-download:hover {
        background-color: #e9ebef !important;
    }
    .choose-file-btn {
        margin-top: 8px;
        padding: 6px 12px;
        border-radius: 6px;
        border: 1px solid #e5e7eb;
        background: #fff;
        font-size: 13px;
        font-weight: 500;
        display: inline-flex;
        align-items: baseline;
        gap: 6px;
        cursor: pointer;
    }

    .choose-file-btn:hover {
        background: $color--bg-dashboard;
    }
    @media screen and (max-width: 600px) {
        .card-headers,
        .main {
            display: block !important;
        }
    }

    .auth-prov-wrapper {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        min-height: 50px;
        padding: 15px 10px;
        border: 1px solid #ccc;
        border-radius: 4px;
        cursor: text;
        transition: border-color 0.2s ease;
    }

    .auth-prov-wrapper.focused {
        border-color: $color--accent-1;
    }
    .auth-prov-wrapper .chips {
        display: flex;
        flex-wrap: wrap;
        gap: 6px;
    }
    .auth-prov::placeholder {
        color: #b9b9b9 !important;
        opacity: 1;
    }

    .chip {
        display: flex;
        align-items: center;
        padding: 4px 8px;
        background: $color--accent-1;
        border-radius: 12px;
        font-size: 13px;
        color: #fff;
    }

    .chip .remove {
        margin-left: 10px;
        cursor: pointer;
        font-weight: bold;
    }

    .auth-prov {
        border: none;
        outline: none;
        flex: 1 1 150px;
        width: 100%;
        min-width: 120px;
        font-size: 14px;
        margin-left: 8px;
        color: $color--txt-label;
    }

    .network-users {
        overflow-y: scroll;
        max-height: 210px;
    }
    .network-user {
        display: flex;
        padding: 10px;
        cursor: pointer;
    }
    .network-user > div:last-child {
        @include breakpoint(smartphone-large) {
            width: 200px;
        }
        width: 300px;
        margin-left: 15px;
    }
    .network-user > div:first-child {
        margin-left: 30px;
    }
    .network-user p:first-child {
        font-weight: 500;
    }
    .network-user p:last-child {
        font-weight: 200;
    }
    .network-user:hover {
        background-color: #f5fbfe;
    }
    .network-user-selected {
        background-color: #e8f7ff;
    }

    .search-network-members__message {
        margin: 30px;
        text-align: center;
    }

    .loading-spinner {
        margin: 30px;
        text-align: center;
        font-size: 16px;
        padding: 32px 16px;
    }
</style>
